import { ContractByCustomerRequest } from '@/api/models/BaseRequest';
import {
  BaseContract,
  Contract,
  ContractRequestPayload,
  SearchFormValues,
} from '@/api/models/Contracts';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the state type
export interface ContractState {
  contracts: Contract[];
  recentSearches: Contract[];
  customerContracts: BaseContract[];
  loading: boolean;
  recentSearchLoading: boolean;
  customerContractsLoading: boolean;
  error: string | null;
  searchFilters: SearchFormValues | null;
  isRecentSearch: boolean;
  isResetTriggered: boolean;
}

// Initial state
const initialState: ContractState = {
  contracts: [],
  recentSearches: [],
  customerContracts: [],
  loading: false,
  recentSearchLoading: false,
  customerContractsLoading: false,
  error: null,
  searchFilters: null,
  isRecentSearch: false,
  isResetTriggered : false,
};
export interface ContractPayload {
  endpoint: string; // API endpoint URL
  payload: ContractRequestPayload; // Request parameters
}

// Create a slice
const contractSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    fetchContractsStart(state, _action: PayloadAction<ContractPayload>) {
      state.loading = true;
      state.error = null;
    },
    fetchContractsSuccess(state, action: PayloadAction<Contract[]>) {
      state.contracts =
        Array.isArray(action.payload) && action.payload.length > 0 ? action.payload : [];
      state.loading = false;
      state.error =
        state.contracts.length === 0 ? 'No contracts found for the given criteria.' : null;
      state.isRecentSearch = false;
      state.isResetTriggered = false;
    },
    fetchContractsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.isRecentSearch = false;
      state.isResetTriggered = false;
    },

    // Fetch Recent Searches
    fetchRecentSearchesStart(state, _action: PayloadAction<string>) {
      state.recentSearchLoading = true;
      state.isRecentSearch = true;
      state.searchFilters = null;
    },
    fetchRecentSearchesSuccess(state, action: PayloadAction<Contract[]>) {
      state.recentSearches = action.payload;
      state.recentSearchLoading = false;
      state.isRecentSearch = true;
      state.searchFilters = null;
    },
    fetchRecentSearchesFailure(state, action: PayloadAction<string>) {
      state.recentSearchLoading = false;
      state.error = action.payload;
      state.isRecentSearch = false;
      state.searchFilters = null;
    },

    // Fetch Customer Contracts
    fetchCustomerContractStart(state, _action: PayloadAction<ContractByCustomerRequest>) {
      state.customerContractsLoading = true;
    },
    fetchCustomerContractSuccess(state, action: PayloadAction<BaseContract[]>) {
      state.customerContracts = action.payload;
      state.customerContractsLoading = false;
    },
    fetchCustomerContractFailure(state, action: PayloadAction<string>) {
      state.customerContractsLoading = false;
      state.error = action.payload;
    },
    setSearchFilters(state, action: PayloadAction<SearchFormValues>) {
      state.searchFilters = action.payload;
    },
    setIsRecentSearch(state, action: PayloadAction<boolean>){
      state.isRecentSearch = action.payload;
    },
    setIsResetTriggered(state, action:PayloadAction<boolean>){
      state.isResetTriggered = action.payload;
    }
  },
});

export const {
  fetchContractsStart,
  fetchContractsSuccess,
  fetchContractsFailure,
  fetchRecentSearchesStart,
  fetchRecentSearchesSuccess,
  fetchRecentSearchesFailure,
  fetchCustomerContractStart,
  fetchCustomerContractSuccess,
  fetchCustomerContractFailure,
  setSearchFilters,
  setIsRecentSearch,
  setIsResetTriggered
} = contractSlice.actions;

export default contractSlice.reducer;
